import { createLoomCommandId } from "./loomAuthoringHistory";
import { canonicalLoomDefinitionJson } from "./loomAcceptedDefinitionBinding";
import {
  parseLoomAuthoringCommand,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
} from "./loomAuthoringModel";
import {
  parseLoomAirshowNativePreset,
  type LoomAirshowNativePreset,
} from "./loomAirshowNativePreset";
import {
  isLoomAirshowCatalogVerified,
  LOOM_AIRSHOW_ARCHIVE_SHA256,
  LOOM_AIRSHOW_CATALOG_REVISION,
  LOOM_AIRSHOW_DERIVED_PRESET_IDS,
  markLoomAirshowCatalogVerified,
  type LoomVerifiedCatalog,
} from "./loomAirshowPresetTrust";
import { parseLoomPresetV3Binding } from "./loomPresetV3Binding";
import type { LoomPresetReceipt } from "./loomPresetReceipt";

export type LoomAirshowSupportState = "derived-native-preset" | "source-only" | "invalid-x3d";

export interface LoomAirshowComponent {
  componentId: string;
  name: string;
  type: string;
}

export interface LoomAirshowSourceAsset {
  byteCount: number;
  documentKind: "vsp-geometry" | "x3d";
  formatVersion: number | string | null;
  lengthUnit: string | null;
  sha256: string;
}

export interface LoomAirshowPresetEntry {
  archiveMemberPath: string;
  audit: {
    blockedComponentTypes: readonly string[];
    bucket: string;
    droppedComponentTypes: readonly string[];
    polygonSectionTypes: readonly string[];
  };
  componentCounts: Readonly<Record<string, number>>;
  components: readonly LoomAirshowComponent[];
  id: string;
  label: string;
  nativePreset: LoomAirshowNativePreset | null;
  reasons: readonly string[];
  sourceAsset: LoomAirshowSourceAsset;
  supportState: LoomAirshowSupportState;
}

export interface LoomAirshowPresetCatalog {
  catalogId: "openvsp-airshow-complete-2026-08-13";
  catalogRevision: string;
  corpus: {
    archiveSha256: string;
    exactCurrentCount: 0;
    invalidX3dCount: 1;
    memberCount: 353;
    vspGeometryCount: 352;
  };
  entries: readonly LoomAirshowPresetEntry[];
  schema: "loom.airshow-preset-catalog/v1";
}

export type LoomVerifiedAirshowPresetCatalog = LoomVerifiedCatalog<LoomAirshowPresetCatalog>;

export type LoomAirshowCatalogState =
  | { kind: "available"; catalog: LoomVerifiedAirshowPresetCatalog }
  | { kind: "unavailable"; reason: string };

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  const missing = allowed.filter((key) => !(key in value));
  if (unknown.length || missing.length) throw new Error(`${path} has an unsupported field set`);
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}

function nullableScalar(value: unknown, path: string): number | string | null {
  if (value === null || typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  throw new Error(`${path} must be a string, number, or null`);
}

function integer(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw new Error(`${path} must be a nonnegative integer`);
  return value;
}

function sha256(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^(?:sha256:)?[a-f0-9]{64}$/.test(result)) throw new Error(`${path} must be a SHA-256 identity`);
  return result;
}

function entryId(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^airshow\/[a-z0-9._-]+\/[a-z0-9._-]+$/.test(result)) throw new Error(`${path} is invalid`);
  return result;
}

function memberPath(value: unknown, path: string): string {
  const result = text(value, path);
  if (result.startsWith("/") || result.includes("\\") || result.split("/").includes("..")) throw new Error(`${path} is unsafe`);
  return result;
}

function textArray(value: unknown, path: string): string[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  return value.map((item, index) => text(item, `${path}[${index}]`));
}

function parseComponent(value: unknown, path: string): LoomAirshowComponent {
  const row = record(value, path);
  exactKeys(row, ["componentId", "name", "type"], path);
  return {
    componentId: text(row.componentId, `${path}.componentId`),
    name: text(row.name, `${path}.name`),
    type: text(row.type, `${path}.type`),
  };
}

function parseComponentCounts(value: unknown, path: string): Readonly<Record<string, number>> {
  const row = record(value, path);
  return Object.fromEntries(Object.entries(row).map(([key, count]) => [text(key, `${path}.key`), integer(count, `${path}.${key}`)]));
}

function parseSourceAsset(value: unknown, path: string): LoomAirshowSourceAsset {
  const row = record(value, path);
  exactKeys(row, ["sha256", "byteCount", "documentKind", "formatVersion", "lengthUnit"], path);
  if (row.documentKind !== "vsp-geometry" && row.documentKind !== "x3d") throw new Error(`${path}.documentKind is unsupported`);
  const lengthUnit = nullableScalar(row.lengthUnit, `${path}.lengthUnit`);
  if (typeof lengthUnit === "number") throw new Error(`${path}.lengthUnit must be a string or null`);
  return {
    byteCount: integer(row.byteCount, `${path}.byteCount`),
    documentKind: row.documentKind,
    formatVersion: nullableScalar(row.formatVersion, `${path}.formatVersion`),
    lengthUnit,
    sha256: sha256(row.sha256, `${path}.sha256`),
  };
}

function parseEntry(value: unknown, index: number): LoomAirshowPresetEntry {
  const path = `catalog.entries[${index}]`;
  const row = record(value, path);
  exactKeys(row, ["id", "archiveMemberPath", "label", "sourceAsset", "componentCounts", "components", "audit", "supportState", "reasons", "nativePreset"], path);
  if (!(row.supportState === "derived-native-preset" || row.supportState === "source-only" || row.supportState === "invalid-x3d")) throw new Error(`${path}.supportState is unsupported`);
  if (!Array.isArray(row.components)) throw new Error(`${path}.components must be an array`);
  const audit = record(row.audit, `${path}.audit`);
  exactKeys(audit, ["bucket", "blockedComponentTypes", "polygonSectionTypes", "droppedComponentTypes"], `${path}.audit`);
  const nativePreset = row.nativePreset === null
    ? null : parseLoomAirshowNativePreset(row.nativePreset, `${path}.nativePreset`);
  if ((row.supportState === "derived-native-preset") !== (nativePreset !== null)) throw new Error(`${path} support and native preset disagree`);
  const sourceAsset = parseSourceAsset(row.sourceAsset, `${path}.sourceAsset`);
  if ((row.supportState === "invalid-x3d") !== (sourceAsset.documentKind === "x3d")) throw new Error(`${path} document kind and support disagree`);
  const result: LoomAirshowPresetEntry = {
    archiveMemberPath: memberPath(row.archiveMemberPath, `${path}.archiveMemberPath`),
    audit: {
      blockedComponentTypes: textArray(audit.blockedComponentTypes, `${path}.audit.blockedComponentTypes`),
      bucket: text(audit.bucket, `${path}.audit.bucket`),
      droppedComponentTypes: textArray(audit.droppedComponentTypes, `${path}.audit.droppedComponentTypes`),
      polygonSectionTypes: textArray(audit.polygonSectionTypes, `${path}.audit.polygonSectionTypes`),
    },
    componentCounts: parseComponentCounts(row.componentCounts, `${path}.componentCounts`),
    components: row.components.map((component, componentIndex) => parseComponent(component, `${path}.components[${componentIndex}]`)),
    id: entryId(row.id, `${path}.id`),
    label: text(row.label, `${path}.label`),
    nativePreset,
    reasons: textArray(row.reasons, `${path}.reasons`),
    sourceAsset,
    supportState: row.supportState,
  };
  const componentIds = result.components.map(({ componentId }) => componentId);
  if (new Set(componentIds).size !== componentIds.length) throw new Error(`${path}.components contains duplicate identities`);
  const counts = Object.fromEntries(Object.entries(result.componentCounts).filter(([, count]) => count > 0));
  const actualCounts = Object.fromEntries([...new Set(result.components.map(({ type }) => type))]
    .sort().map((type) => [type, result.components.filter((component) => component.type === type).length]));
  if (canonicalLoomDefinitionJson(counts) !== canonicalLoomDefinitionJson(actualCounts)) throw new Error(`${path}.componentCounts is stale`);
  if (nativePreset) {
    const selected = new Set(nativePreset.selectedComponents.map(({ componentId }) => componentId));
    const omitted = new Set(nativePreset.omittedComponents.map(({ componentId }) => componentId));
    if ([...selected].some((id) => omitted.has(id)) || new Set([...selected, ...omitted]).size !== componentIds.length
        || componentIds.some((id) => !selected.has(id) && !omitted.has(id))
        || nativePreset.adaptations.some(({ componentId }) => !componentIds.includes(componentId))
        || Math.abs(nativePreset.normalization.metresPerNativeUnit
          * nativePreset.normalization.sourceBodyLengthNative - 10) > 1e-9) {
      throw new Error(`${path}.nativePreset provenance does not partition and bind the source components`);
    }
    if (nativePreset.contractVersion === "v3") {
      const sourceBinding = nativePreset.definition.sourceBinding;
      if (sourceBinding.sourceAssetSha256.replace(/^sha256:/, "")
            !== result.sourceAsset.sha256.replace(/^sha256:/, "")
          || canonicalLoomDefinitionJson(sourceBinding.selectedComponents)
            !== canonicalLoomDefinitionJson(nativePreset.selectedComponents)
          || canonicalLoomDefinitionJson(sourceBinding.normalization)
            !== canonicalLoomDefinitionJson(nativePreset.normalization)) {
        throw new Error(`${path}.nativePreset v3 source binding is stale`);
      }
    }
  }
  return result;
}

export function parseLoomAirshowPresetCatalog(value: unknown): LoomAirshowPresetCatalog {
  const row = record(value, "catalog");
  exactKeys(row, ["schema", "catalogId", "catalogRevision", "corpus", "entries"], "catalog");
  if (row.schema !== "loom.airshow-preset-catalog/v1" || row.catalogId !== "openvsp-airshow-complete-2026-08-13") throw new Error("catalog identity is stale or unsupported");
  const corpus = record(row.corpus, "catalog.corpus");
  exactKeys(corpus, ["archiveSha256", "memberCount", "vspGeometryCount", "invalidX3dCount", "exactCurrentCount"], "catalog.corpus");
  if (corpus.memberCount !== 353 || corpus.vspGeometryCount !== 352 || corpus.invalidX3dCount !== 1 || corpus.exactCurrentCount !== 0) throw new Error("catalog corpus identity is stale");
  if (!Array.isArray(row.entries) || row.entries.length !== 353) throw new Error("catalog must contain all 353 Airshow members");
  const entries = row.entries.map(parseEntry);
  if (new Set(entries.map(({ id }) => id)).size !== entries.length
      || new Set(entries.map(({ archiveMemberPath }) => archiveMemberPath)).size !== entries.length) throw new Error("catalog contains duplicate identities");
  if (entries.filter(({ supportState }) => supportState === "invalid-x3d").length !== 1
      || entries.filter(({ supportState }) => supportState !== "invalid-x3d").length !== 352) {
    throw new Error("catalog support totals are stale");
  }
  return {
    schema: row.schema,
    catalogId: row.catalogId,
    catalogRevision: sha256(row.catalogRevision, "catalog.catalogRevision"),
    corpus: {
      archiveSha256: sha256(corpus.archiveSha256, "catalog.corpus.archiveSha256"),
      exactCurrentCount: 0,
      invalidX3dCount: 1,
      memberCount: 353,
      vspGeometryCount: 352,
    },
    entries,
  };
}

async function sha256Revision(value: unknown): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("SHA-256 verification is unavailable");
  const encoded = new TextEncoder().encode(canonicalLoomDefinitionJson(value));
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", encoded));
  return `sha256:${Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export async function verifyLoomAirshowPresetCatalogContent(
  value: unknown,
): Promise<LoomAirshowPresetCatalog> {
  const source = record(value, "catalog");
  const catalog = parseLoomAirshowPresetCatalog(source);
  const entries = source.entries;
  const catalogRevision = await sha256Revision({
    catalogId: source.catalogId, corpus: source.corpus, entries,
  });
  if (catalogRevision !== catalog.catalogRevision) throw new Error("catalog revision is stale");
  if (!Array.isArray(entries)) throw new Error("catalog entries are unavailable");
  for (const [index, entryValue] of entries.entries()) {
    const entry = record(entryValue, `catalog.entries[${index}]`);
    if (entry.supportState !== "derived-native-preset") continue;
    const preset = record(entry.nativePreset, `catalog.entries[${index}].nativePreset`);
    const definition = record(preset.definition, `catalog.entries[${index}].nativePreset.definition`);
    const v3 = definition.schema === "loom.wing-body-definition/v2";
    const evaluatorId = v3 ? preset.evaluatorId : "loom.native-wing-body/v2";
    const topology = v3
      ? preset.topology : "canonical-starboard-wing-fuselage-union";
    const definitionRevision = await sha256Revision({
      definition: preset.definition, evaluatorId, topology,
    });
    if (definitionRevision !== preset.definitionRevision) {
      throw new Error(`catalog entry ${entry.id} has a stale definition revision`);
    }
    const commonPresetRevision = {
      sourceScope: preset.sourceScope,
      selectedComponents: preset.selectedComponents,
      omittedComponents: preset.omittedComponents,
      adaptations: preset.adaptations,
      normalization: preset.normalization,
      definitionRevision: preset.definitionRevision,
      definition: preset.definition,
    };
    const presetRevision = await sha256Revision(v3 ? {
      ...commonPresetRevision,
      evaluatorId: preset.evaluatorId,
      topology: preset.topology,
      unionAlgorithmId: preset.unionAlgorithmId,
      acceptedMeshSchema: preset.acceptedMeshSchema,
    } : commonPresetRevision);
    if (presetRevision !== preset.presetRevision) {
      throw new Error(`catalog entry ${entry.id} has a stale preset revision`);
    }
  }
  return catalog;
}

export async function verifyLoomAirshowPresetCatalog(raw: string): Promise<LoomAirshowCatalogState> {
  try {
    const catalog = await verifyLoomAirshowPresetCatalogContent(JSON.parse(raw));
    if (catalog.catalogRevision !== LOOM_AIRSHOW_CATALOG_REVISION
        || catalog.corpus.archiveSha256 !== LOOM_AIRSHOW_ARCHIVE_SHA256) {
      throw new Error("catalog is not the pinned Airshow corpus revision");
    }
    const derivedIds = catalog.entries
      .filter(({ supportState }) => supportState === "derived-native-preset")
      .map(({ id }) => id).sort();
    if (canonicalLoomDefinitionJson(derivedIds)
        !== canonicalLoomDefinitionJson([...LOOM_AIRSHOW_DERIVED_PRESET_IDS].sort())) {
      throw new Error("catalog runnable identities do not match the pinned revision");
    }
    return { kind: "available", catalog: markLoomAirshowCatalogVerified(catalog) };
  } catch (error) {
    return { kind: "unavailable", reason: error instanceof Error ? error.message : "Airshow catalogue revision verification failed" };
  }
}

export async function loadBundledLoomAirshowPresetCatalog(): Promise<LoomAirshowCatalogState> {
  try {
    const { default: raw } = await import("./loomAirshowPresetCatalog.json?raw");
    return verifyLoomAirshowPresetCatalog(raw);
  } catch (error) {
    return { kind: "unavailable", reason: error instanceof Error ? error.message : "Airshow catalogue could not be loaded" };
  }
}

export function createLoomAirshowPresetApplyCommand(
  catalog: LoomVerifiedAirshowPresetCatalog,
  entry: LoomAirshowPresetEntry,
  document: LoomAuthoringDocument,
): LoomAuthoringCommand {
  if (!isLoomAirshowCatalogVerified(catalog)) throw new Error("Airshow catalog has not passed pinned integrity verification");
  const current = catalog.entries.find(({ id }) => id === entry.id);
  if (current !== entry || !entry.nativePreset || entry.supportState !== "derived-native-preset") throw new Error("Airshow preset selection is stale or not runnable");
  const preset = entry.nativePreset;
  const receipt: LoomPresetReceipt = {
    schema: "loom.preset-receipt/v1",
    adaptations: preset.adaptations,
    archiveMemberPath: entry.archiveMemberPath,
    catalogId: catalog.catalogId,
    catalogRevision: catalog.catalogRevision,
    definitionRevision: preset.definitionRevision,
    entryId: entry.id,
    entryLabel: entry.label,
    normalization: preset.normalization,
    omittedComponents: preset.omittedComponents,
    presetRevision: preset.presetRevision,
    selectedComponents: preset.selectedComponents,
    sourceAssetSha256: entry.sourceAsset.sha256,
    sourceScope: preset.sourceScope,
  };
  const presetBinding = preset.contractVersion === "v3" ? parseLoomPresetV3Binding({
    schema: "loom.preset-binding/v1",
    acceptedAssetSha256: preset.acceptedAssetSha256,
    acceptedMeshSchema: preset.acceptedMeshSchema,
    definition: preset.definition,
    evaluatorId: preset.evaluatorId,
    presetReceipt: receipt,
    qcReceipt: preset.qcReceipt,
    topology: preset.topology,
    unionAlgorithmId: preset.unionAlgorithmId,
  }) : undefined;
  return parseLoomAuthoringCommand({
    schema: "loom-authoring-command/v1",
    commandId: createLoomCommandId("gui-airshow-preset", document.revision),
    expectedRevision: document.revision,
    kind: "preset.apply",
    nodes: preset.replacement.nodes,
    rootNodeIds: preset.replacement.rootNodeIds,
    receipt,
    ...(presetBinding ? { presetBinding } : {}),
  });
}
